import { captureException } from '@sentry/react'
import { getAIVideoProjects_API } from 'api'
import { PROJECT_MEDIA_TYPES } from 'consts'
import aiAvatarSlice from 'store/features/aiAvatar/slice'
import { avatarVoiceInitial } from 'store/features/aiAvatar/initialState'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { clampQualityForFreeUser } from 'store/features/aiAvatar/utils'
import { cacheSelectors } from 'store/features/cache/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { TAppDispatch, TGetState, TRootState } from 'store/types'
import { AvatarSource, IAvatarGenerationParams } from 'store/types/aiAvatar'
import { withAppThunks } from 'store/with-app-thunks'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { IAIVideoProject } from 'types/aiVideo'

const { actions } = aiAvatarSlice

const getAIAvatarByAssetId = (assetId: number) =>
  withAppThunks(
    appThunks =>
      async (
        dispatch: TAppDispatch,
        getState: TGetState
      ): Promise<IAIVideoProject | null> => {
        const state = getState()
        const aiAvatarCache = cacheSelectors.aiAvatarCache(state)
        const existingItem = aiAvatarCache[assetId]

        if (existingItem) {
          return existingItem
        }

        try {
          const { data } = await getAIVideoProjects_API({
            projectIds: [assetId]
          })

          if (data.length === 0) {
            return null
          }

          dispatch(appThunks.cacheThunks.addToAiAvatarCache(data))

          return data[0]
        } catch (error) {
          if (!(error instanceof ApiError)) {
            captureException(new ReduxThunkError(error))
          }
          return null
        }
      }
  )

const setGenerationParams =
  (params: IAvatarGenerationParams) => (dispatch: TAppDispatch) => {
    dispatch(actions.setGenerationParams(params))
  }

// Maps an avatar (ai-video) project's saved data into generate-form params.
// Returns null when the project has no voiceover audio (the speech/voice source
// the form needs). `targetMediaItemId` ties the params to a timeline clip for an
// in-place regen, or is null for a fresh generation prefilled from history.
const buildAvatarGenerationParams = (
  data: IAIVideoProject,
  state: TRootState,
  targetMediaItemId: string | null
): IAvatarGenerationParams | null => {
  const audios = data.referenceMedias.audios

  if (!audios) {
    return null
  }
  const { text, speakerId, langCode } = audios[0]

  return {
    avatar: {
      // placeholder — only read to highlight a public preset
      id: String(data.id),
      url: data.imageUrl!,
      // non-PUBLIC so the preset-highlight is skipped; url drives everything
      source: AvatarSource.MY_FILES
    },
    voice: {
      ...avatarVoiceInitial,
      langCode,
      speakerId
    },
    // A free user can select a clip rendered at a premium quality (made
    // while subscribed, or from a template/duplicate); clamp before it lands
    // in the form so the dropdown shows a value they can actually use. The
    // generate boundary (`requestAvatarGeneration`) clamps again as a guard.
    quality: clampQualityForFreeUser(
      data.quality,
      aiAvatarSelectors.avatarPossibleQualities(state),
      userSelectors.isFreeUser(state)
    ),
    speechText: text,
    // Pre-fill the aspect-ratio toggle with the clip's original ratio so a
    // regeneration keeps it (null falls back to the project ratio).
    aspectRatio: data.aspectRatio,
    targetMediaItemId
  }
}

const setRegenerationForm =
  (mediaItemId: string) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    try {
      const mediaItems = projectDataSelectors.mediaItems(state)
      const targetMedia = mediaItems.find(
        item =>
          item.id === mediaItemId && item.type === PROJECT_MEDIA_TYPES.aiAvatar
      )
      if (!targetMedia) {
        return
      }

      // `dispatch` of a `withAppThunks` thunk doesn't carry the return type, so
      // a cast is unavoidable — but cast to the ACCURATE nullable type and then
      // narrow. `getAIAvatarByAssetId` returns null when the project isn't found
      // or the fetch fails; bail instead of dereferencing (the old
      // `as IAIVideoProject` cast hid the null and threw on `.referenceMedias`,
      // silently breaking the regen form).
      const data = (await dispatch(
        getAIAvatarByAssetId(Number(targetMedia.assetId))
      )) as IAIVideoProject | null

      if (!data) {
        return
      }

      const params = buildAvatarGenerationParams(data, state, mediaItemId)

      if (!params) {
        return
      }

      dispatch(setGenerationParams(params))
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

// Prefills the generate form from an avatar (ai-video) asset id directly, with
// no timeline clip required — used by the AI History "Re-Generate" action, where
// the chosen clip generally isn't on the timeline. `targetMediaItemId` is null so
// a Generate click produces a fresh clip (mirrors aiVideo's same-named thunk).
const setGenerationParamsByAssetId =
  (assetId: number | string) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const numericAssetId = Number(assetId)
    if (isNaN(numericAssetId)) {
      return
    }
    try {
      const data = (await dispatch(
        getAIAvatarByAssetId(numericAssetId)
      )) as IAIVideoProject | null

      if (!data) {
        return
      }

      const params = buildAvatarGenerationParams(data, getState(), null)

      if (!params) {
        return
      }

      dispatch(setGenerationParams(params))
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

export const formThunks = {
  getAIAvatarByAssetId,
  setRegenerationForm,
  setGenerationParamsByAssetId,
  setGenerationParams
}
